import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuTrigger,
  ContextMenuSub,
  ContextMenuSubTrigger,
  ContextMenuSubContent,
} from '@/components/ui/context-menu';
import { FM_CONTEXT_MENU_SURFACE } from '@/components/ui/menu-surface';
import { DeleteConfirmDialog } from '@/components/canvas/components/delete-confirm-dialog';
import { cn } from '@/lib/utils';
import { useNodeOperations } from '@/providers/node-operations';
import { quickConnectMultiple, deleteSelectedNodes, centerViewportOnNode } from '@/components/tools/lib/nodes';
import { EditableNodeTitle } from '@/components/nodes/editable-title';
import { Handle, Position, useReactFlow, useStore } from '@xyflow/react';
import { useProject } from '@/providers/project';
import { useUser } from '@/hooks/use-user';
import { CopyIcon, CopyPlus, EyeIcon, TrashIcon, MessageSquare, Image as ImageIcon, Music, Play as PlayIcon, Square, ArrowLeft, ArrowRight, Lock as LockIcon, ChevronDown, ChevronRight, StickyNote, Upload, File, Wrench, SendHorizonal } from 'lucide-react';
import { nanoid } from 'nanoid';
import { memo, type ReactNode, useCallback, useMemo, useState } from 'react';
import { useI18n } from '@/providers/i18n';
import { NodeToolbar } from './toolbar';
import { imageModels } from '@flowmuse/models/image';
import { videoModels } from '@flowmuse/models/video';
import { getProviderIcon } from '@/lib/providers';
import { speechModels } from '@flowmuse/models/speech';
import { textModels } from '@/lib/models/text';
import { useModelLabelById } from '@/lib/models/use-model-label';
import { resolveMediaSizeLabel } from '@flowmuse/models/shared/image-size';
import { selectNodeSelectedById } from './store-selectors';
import { useIsMultiSelect } from '@/components/canvas/utils/multi-select-store';
import { useIsConnectDropTarget } from '@/components/canvas/utils/connect-target-store';
import { useIsAutoRunActive } from '@/lib/auto-run/active-store';
import { useGenerationEffects } from '@/hooks/use-generation-effects';
import { SaveToLibrarySubmenu } from '@/components/nodes/shared/save-to-library-submenu';

type NodeLayoutProps = {
  children: ReactNode;
  id: string;
  data?: Record<string, unknown> & {
    model?: string;
    source?: string;
    generated?: object;
  };
  title: string;
  type: string;
  /**
   * 折叠态内容摘要（可选）。
   * - 若提供：折叠时优先渲染该摘要，而不是默认的 “Collapsed” 行。
   * - 建议保持轻量，避免在折叠态仍挂载重组件（视频/编辑器/大表单等）。
   */
  collapsedSummary?: ReactNode;
  /**
   * 折叠态外框固定比例（可选）。用于避免折叠后尺寸跟随节点内部/工具栏比例变化。
   * 例如视频/图像/文本等节点折叠态固定为 "16/9"。
   */
  collapsedAspectRatio?: string | number;
  /**
   * 可选：用于外层容器计算默认尺寸的宽高比（例如 "16/9"、"1/1"、"1024/768" 或数字）。
   * 如提供，则按“短边 = w-sm(≈384px)”规则确定长边；否则回退为固定宽度 w-sm。
   */
  aspectRatio?: string | number;
  toolbar?: {
    tooltip?: string;
    children: ReactNode;
  }[];
  className?: string;
  variant?: 'default' | 'modern';
  titleBubble?: ReactNode;
  contextMenuItems?: { label: string; onClick: () => void; icon?: ReactNode; disabled?: boolean }[];
  /**
   * 极简模式：隐藏节点上方的标题栏（标题/折叠按钮）与下方的模型徽标。
   * 用于"临时比对节点"等纯展示场景。
   */
  chromeless?: boolean;
};

const inferModelBadge = (
  nodeType: string,
  rawModelId: unknown
): { label: string; Icon?: React.ComponentType<any> } | undefined => {
  const modelId = typeof rawModelId === 'string' && rawModelId.trim() ? rawModelId.trim() : undefined;
  if (!modelId) return undefined;

  const fallbackLabel = (() => {
    try {
      if (modelId.includes('/')) return modelId.split('/').pop() || modelId;
      return modelId;
    } catch {
      return modelId;
    }
  })();

  try {
    if (nodeType === 'image') {
      const m = (imageModels as any)?.[modelId] ?? (modelId.includes('/') ? (imageModels as any)?.[modelId.split('/').pop() as any] : undefined);
      const Icon = (m?.icon ?? m?.chef?.icon ?? getProviderIcon(m?.chef?.id)) as any;
      return { label: fallbackLabel, Icon };
    }
    if (nodeType === 'video') {
      const m = (videoModels as any)?.[modelId] ?? (modelId.includes('/') ? (videoModels as any)?.[modelId.split('/').pop() as any] : undefined);
      const Icon = (m?.icon ?? m?.chef?.icon ?? getProviderIcon(m?.chef?.id)) as any;
      return { label: fallbackLabel, Icon };
    }
    if (nodeType === 'audio') {
      const m = (speechModels as any)?.[modelId] ?? (modelId.includes('/') ? (speechModels as any)?.[modelId.split('/').pop() as any] : undefined);
      const Icon = (m?.icon ?? m?.chef?.icon ?? getProviderIcon(m?.chef?.id)) as any;
      return { label: fallbackLabel, Icon };
    }
    if (nodeType === 'text') {
      const m = (textModels as any)?.[modelId] ?? (modelId.includes('/') ? (textModels as any)?.[modelId.split('/').pop() as any] : undefined);
      const Icon = (m?.icon ?? m?.chef?.icon) as any;
      return { label: fallbackLabel, Icon };
    }
  } catch {}

  return { label: fallbackLabel };
};

const NodeTitleLogo = ({ type }: { type: string }) => {
  const Icon = (() => {
    switch (String(type)) {
      case 'text':
        return MessageSquare;
      case 'image':
        return ImageIcon;
      case 'audio':
        return Music;
      case 'video':
        return PlayIcon;
      case 'stickyNote':
        return StickyNote;
      case 'file':
        return File;
      case 'upload':
        return Upload;
      case 'tool':
        return Wrench;
      default:
        return null;
    }
  })();

  if (!Icon) return null;
  return <Icon className="h-4 w-4 shrink-0 opacity-80 pointer-events-none" />;
};

const NodeLayoutImpl = ({
  children,
  type,
  id,
  data,
  collapsedSummary,
  collapsedAspectRatio,
  aspectRatio,
  toolbar,
  title,
  className,
  variant = 'default',
  titleBubble,
  contextMenuItems,
  chromeless = false,
}: NodeLayoutProps) => {
  const { t } = useI18n();
  const { deleteElements, setCenter, getNode, updateNode, getNodes, getEdges, setNodes, addEdges, setEdges } = useReactFlow();
  const selectIsSelected = useMemo(() => selectNodeSelectedById(id), [id]);
  const isSelected = useStore(selectIsSelected);
  // Whether the canvas-wide auto-run scheduler is currently processing this
  // node — drives a pulsating halo so the user can follow the run visually.
  const isAutoRunActive = useIsAutoRunActive(id);
  const [generationEffectsEnabled] = useGenerationEffects();
  const { addNode, duplicateNode, cloneNode } = useNodeOperations();
  const project = useProject();
  const { user } = useUser();
  const [bulkDeleteConfirm, setBulkDeleteConfirm] = useState(false);
  // Perf (Phase 7.1): "sticky activate" pattern for the per-node Radix
  // ContextMenu. The menu items subtree is huge (5+ submenus, 20+ items, lots
  // of icons) but is only used when the user actually right-clicks. We render
  // an empty <ContextMenuContent /> by default so Radix can still mount its
  // root + trigger on every node, but the heavy items subtree only populates
  // after the FIRST right-click. After that we keep it mounted to avoid
  // mount/unmount jank on subsequent right-clicks.
  const [ctxMenuActivated, setCtxMenuActivated] = useState(false);
  const activateCtxMenu = useCallback(() => {
    if (!ctxMenuActivated) setCtxMenuActivated(true);
  }, [ctxMenuActivated]);

  const handleQuickConnect = (targetType: 'text' | 'image' | 'audio' | 'video' | 'stickyNote') => {
    try {
      const node = getNode(id);
      if (!node) return;
      const baseX = node.position.x ?? 0;
      const baseY = node.position.y ?? 0;
      const width = (node as any)?.measured?.width ?? 300;
      const gap = 120;
      const position = { x: baseX + width + gap, y: baseY };
      // 对于 text 类型，设置为 transform 模式（文本模型节点）
      const nodeData = targetType === 'text' ? { viewMode: 'transform' } : {};
      const newId = addNode(targetType, { position, selected: true, data: nodeData });
      if (!newId) return;
      try {
        addEdges?.([{ id: nanoid(), source: id, target: newId, type: 'animated' as const }]);
      } catch {}
    } catch {}
  };

  const handleFocus = () => {
    centerViewportOnNode(setCenter, getNode(id), { anchor: 'top', duration: 1000 });
  };

  const handleDisconnectIncoming = () => {
    try {
      // 保持当前为 Transform，不因断开入边退回 Primitive
      try {
        const node = getNode(id) as any;
        const prevData = (node?.data ?? (data as any) ?? {}) as Record<string, unknown>;
        // 设置 viewMode 为 transform，确保节点永久保持 transform 状态
        updateNode(id, { data: { ...prevData, viewMode: 'transform' } });
      } catch {}
      // 清理镜像角色：从当前节点的 edgeFrameRoles 移除所有入边来源对应的标记
      try {
        const removed = (getEdges?.() || []).filter((e: any) => String(e?.target) === String(id));
        const sources = new Set<string>(removed.map((e: any) => String(e?.source)));
        if (sources.size) {
          setNodes?.((nodes) => nodes.map((n) => {
            if (String(n.id) !== String(id)) return n;
            const ndata: any = (n.data as any) || {};
            const map: Record<string, any> = { ...(ndata.edgeFrameRoles || {}) };
            sources.forEach((src) => { try { delete map[src]; } catch {} });
            return { ...n, data: { ...ndata, edgeFrameRoles: map } } as any;
          }));
        }
      } catch {}
      setEdges?.((edges) => edges.filter((e) => (e as any)?.target !== id));
    } catch {}
  };

  const handleDisconnectOutgoing = () => {
    try {
      // 清理镜像角色：从所有目标节点的 edgeFrameRoles 中移除当前源(id)的标记
      try {
        const removed = (getEdges?.() || []).filter((e: any) => String(e?.source) === String(id));
        const targets = new Set<string>(removed.map((e: any) => String(e?.target)));
        if (targets.size) {
          setNodes?.((nodes) => nodes.map((n) => {
            if (!targets.has(String(n.id))) return n;
            const ndata: any = (n.data as any) || {};
            const map: Record<string, any> = { ...(ndata.edgeFrameRoles || {}) };
            try { delete map[String(id)]; } catch {}
            return { ...n, data: { ...ndata, edgeFrameRoles: map } } as any;
          }));
        }
      } catch {}
      setEdges?.((edges) => edges.filter((e) => (e as any)?.source !== id));
    } catch {}
  };

  const handleDeleteSingle = () => {
    // 单个节点删除：不需要二次确认
    try {
      deleteElements({ nodes: [{ id }] });
    } catch {}
  };

  // 运行中（processing）时禁用部分菜单动作
  // Use data prop directly instead of getNode() to avoid redundant store reads
  const isProcessing = Boolean((data as any)?.status === 'processing');

  const getModelLabelById = useModelLabelById();
  const modelBadge = useMemo(() => {
    try {
      const badge = inferModelBadge(type, (data as any)?.model);
      if (!badge) return undefined;
      const localized = getModelLabelById((data as any)?.model);
      return localized ? { ...badge, label: localized } : badge;
    } catch {
      return undefined;
    }
  }, [type, (data as any)?.model, getModelLabelById]);

  // 真实分辨率徽标（节点外侧左下角）。仅图像/视频（含上传的图像/视频）展示。
  // 分辨率来源优先级统一封装在 resolveMediaSizeLabel：generated.size → 顶层 size → width/height。
  const sizeBadge = useMemo(() => {
    const isMedia =
      type === 'image' ||
      type === 'video' ||
      (type === 'upload' && ((data as any)?.fileType === 'image' || (data as any)?.fileType === 'video'));
    if (!isMedia) return undefined;
    return resolveMediaSizeLabel(data);
  }, [type, (data as any)?.fileType, (data as any)?.generated?.size, (data as any)?.size, (data as any)?.width, (data as any)?.height]);

  // 锁定状态来自节点 data.locked — use prop directly
  const isLocked = Boolean((data as any)?.locked);

  const isOriginalOwner = useMemo(() => {
    try {
      const originOwnerId = (project as any)?.originOwnerUserId ?? (project as any)?.userId;
      return Boolean(user?.id) && String(user?.id) === String(originOwnerId ?? '');
    } catch {
      return false;
    }
  }, [project, user?.id]);

  const isMenuDisabled = isLocked && !isOriginalOwner;

  const isMenuDisabledAll = useMemo(() => {
    if (isMenuDisabled) return true;
    try {
      const flag = Boolean(((project as any)?.content as any)?.share?.disableCreateNodes);
      return flag && !isOriginalOwner;
    } catch {
      return false;
    }
  }, [isMenuDisabled, project, isOriginalOwner]);

  // Use data prop directly instead of getNode() to avoid redundant store reads
  const isCollapsed = Boolean((data as any)?.collapsed);

  const toggleCollapsed = () => {
    try {
      const node = getNode(id) as any;
      const prev = (node?.data ?? (data as any) ?? {}) as Record<string, unknown>;
      const next = !Boolean((prev as any)?.collapsed);
      updateNode(id, { data: { ...prev, collapsed: next } });
    } catch {}
  };

  const handleQuickConnectMulti = (targetType: 'text' | 'image' | 'audio' | 'video' | 'stickyNote') => {
    // 仅对“当前选中”的节点批量连接
    const selectedNodesOnly = getNodes().filter((n) => n.selected);
    if (!selectedNodesOnly.length) return;

    // 先取消现有节点的选中状态
    setNodes((prev: any[]) => prev.map((n) => ({ ...n, selected: false })));

    // 执行批量快速连接（新建节点保持选中）
    const createdIds = quickConnectMultiple({ selectedNodes: selectedNodesOnly as any, addNode, setEdges: setEdges as any, targetType });

    // 处理可能的异步选择回弹：下一轮事件循环只保留新建节点为选中
    try {
      setTimeout(() => {
        const keep = new Set(createdIds || []);
        setNodes((prev: any[]) => prev.map((n) => ({ ...n, selected: keep.has(String(n.id)) })));
      }, 0);
    } catch {}
  };

  const handleDeleteSelected = () => {
    deleteSelectedNodes({ nodes: getNodes() as any, setNodes: setNodes as any, setEdges: setEdges as any });
  };

  const isMultiSelect = useIsMultiSelect();
  // 连线拖拽时，指针进入本节点范围且可连接 → 点亮“自动运行光环”提示可在此释放。
  const isConnectTarget = useIsConnectDropTarget(id);
  const getSelectedCount = useCallback(() => {
    try {
      return getNodes().filter((n) => n.selected).length;
    } catch { return 0; }
  }, [getNodes]);

  // 解析宽高比，按“短边 = w-sm(≈384px)”计算容器宽高
  const containerStyle = useMemo<React.CSSProperties | undefined>(() => {
    try {
      const ratioInput =
        isCollapsed && typeof collapsedAspectRatio !== 'undefined'
          ? collapsedAspectRatio
          : aspectRatio;
      if (typeof ratioInput === 'undefined' || ratioInput === null) return undefined;
      let r: number | undefined;
      if (typeof ratioInput === 'number') {
        r = Number(ratioInput);
      } else if (typeof ratioInput === 'string') {
        const raw = ratioInput.trim();
        if (/^\d+(\.\d+)?[\/:]\d+(\.\d+)?$/.test(raw)) {
          const sep = raw.includes('/') ? '/' : ':';
          const [w, h] = raw.split(sep).map(Number);
          if (Number.isFinite(w) && Number.isFinite(h) && h > 0) r = w / h;
        } else if (/^\d+(\.\d+)?$/.test(raw)) {
          r = Number(raw);
        }
      }
      if (!r || !Number.isFinite(r) || r <= 0) return undefined;
      const style: any = { ['--node-ratio']: String(r) };
      // 折叠态默认使用更小的短边，避免固定 16:9 时节点过大
      if (isCollapsed) {
        style['--node-short-side'] = '9.5';
      }
      if (r > 1) {
        // 横向：固定高 = 短边（rem），宽 = 短边 * 比例
        style.width = 'calc(var(--node-short-side, 24) * 1rem * var(--node-ratio))';
        style.height = 'calc(var(--node-short-side, 24) * 1rem)';
        return style as React.CSSProperties;
      }
      // 纵向或正方：固定宽 = 短边（rem），高 = 短边 / 比例
      style.width = 'calc(var(--node-short-side, 24) * 1rem)';
      style.height = 'calc(var(--node-short-side, 24) * 1rem / var(--node-ratio))';
      return style as React.CSSProperties;
    } catch {
      return undefined;
    }
  }, [isCollapsed, collapsedAspectRatio, aspectRatio]);

  const effectiveContainerStyle = useMemo<React.CSSProperties | undefined>(() => {
    if (!isCollapsed) return containerStyle;
    try {
      return {
        ...(containerStyle || {}),
        height:
          typeof collapsedAspectRatio !== 'undefined'
            ? (containerStyle as any)?.height
            : (collapsedSummary ? 'auto' : '3.5rem'),
      } as React.CSSProperties;
    } catch {
      return {
        height:
          typeof collapsedAspectRatio !== 'undefined'
            ? undefined
            : (collapsedSummary ? 'auto' : '3.5rem'),
      } as React.CSSProperties;
    }
  }, [isCollapsed, collapsedAspectRatio, collapsedSummary, containerStyle]);

  return (
    <>
      {type !== 'drop' && !isCollapsed && !chromeless && !!toolbar?.length && !isMultiSelect && (
        <NodeToolbar id={id} items={toolbar} />
      )}
      {type !== 'file' && type !== 'tweet' && type !== 'upload' && (
        <Handle
          type="target"
          position={Position.Left}
          isConnectable={!isMenuDisabled}
          className={isMenuDisabled ? 'opacity-50 pointer-events-none' : undefined}
        />
      )}
      <ContextMenu>
        <ContextMenuTrigger onContextMenu={activateCtxMenu}>
          <div
            className={cn(
              'relative',
              !effectiveContainerStyle && 'size-full h-auto w-sm',
              // 仅对“无自定义摘要”的折叠节点做紧凑高度兜底
              isCollapsed && !collapsedSummary && !effectiveContainerStyle && 'h-14'
            )}
            style={effectiveContainerStyle}
          >
            {type !== 'drop' && !chromeless && (
              <div className="-translate-y-full top-0 absolute right-0 left-0 z-30 flex shrink-0 items-center justify-between h-7 md:h-9 pointer-events-auto">
                <div className="flex min-w-0 items-center gap-2">
                  <NodeTitleLogo type={type} />
                  <EditableNodeTitle
                    id={id}
                    data={data as any}
                    defaultTitle={title}
                    variant={variant}
                    displayMaxChars={isCollapsed ? 12 : 16}
                  />
                </div>
                <div className="shrink-0 flex items-center gap-2">
                  {(() => {
                    const bubble = titleBubble ?? (isLocked ? (
                      <div className="rounded-full border border-primary/30 bg-primary/10 backdrop-blur px-1.5 py-0 h-6 text-[11px] leading-none text-primary flex items-center gap-1">
                        <LockIcon size={10} />
                        <span>{t('Locked')}</span>
                      </div>
                    ) : null);
                    return bubble ? <div className="shrink-0">{bubble}</div> : null;
                  })()}
                  <button
                    type="button"
                    aria-label={isCollapsed ? t('Expand') : t('Collapse')}
                    title={isCollapsed ? t('Expand') : t('Collapse')}
                    disabled={isMenuDisabledAll || type === 'drop'}
                    className={cn(
                      'nodrag inline-flex items-center justify-center transition-all',
                      variant === 'modern' 
                        ? 'rounded-xl bg-transparent text-foreground/90 h-8 px-3 hover:text-foreground' 
                        : 'rounded-md border bg-background/70 text-foreground/90 hover:bg-background/90 backdrop-blur px-2.5 h-7 shadow-sm border-primary/60 hover:bg-primary/10',
                      (isMenuDisabledAll || type === 'drop') && 'opacity-50 pointer-events-none'
                    )}
                    onPointerDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleCollapsed();
                    }}
                  >
                    {isCollapsed ? (
                      <ChevronRight size={14} />
                    ) : (
                      <ChevronDown size={14} />
                    )}
                    <span
                      className={cn(
                        "ml-1 text-xs text-foreground/80",
                        variant === "modern" ? "tracking-tight font-bold" : "font-mono tracking-tight font-semibold"
                      )}
                    >
                      {isCollapsed ? t('Expand') : t('Collapse')}
                    </span>
                  </button>
                </div>
              </div>
            )}
            {/* Auto-run halo — rendered as a sibling that is positioned
                exactly on top of the node container (inset-0, same border
                radius) so its box-shadow radiates directly from the node's
                edge with no visible gap. Sits behind the node via z-0 vs.
                z-10 so it can't cover any node content. */}
            {isConnectTarget || (generationEffectsEnabled && (isAutoRunActive || isProcessing)) ? (
              <div
                aria-hidden
                className={cn(
                  'pointer-events-none absolute inset-0 z-0',
                  'fm-auto-run-halo',
                  variant === 'modern' ? 'rounded-3xl' : 'rounded-lg',
                )}
              />
            ) : null}
            <div
              className={cn(
                'node-container relative z-10 flex size-full flex-col transition-all overflow-hidden',
                variant === 'modern' 
                  ? 'rounded-3xl bg-sidebar/95 backdrop-blur-2xl border-none shadow-2xl ring-1 ring-sidebar-border/30' 
                  : 'rounded-lg bg-card ring-1 ring-border',
                className
              )}
            >
              {isCollapsed ? (
                collapsedSummary ?? (
                  <div className="flex h-full w-full items-center justify-between px-3 text-sm">
                    <span className="text-muted-foreground">{t('Collapsed')}</span>
                  </div>
                )
              ) : children}
            </div>

            {/* Size badge (outside node, bottom-left) */}
            {!isCollapsed && !chromeless && sizeBadge && !isSelected ? (
              <div className="pointer-events-none absolute bottom-0 left-0 translate-y-[calc(100%+0.5rem)] z-30">
                <div
                  className={cn(
                    "text-xs text-foreground/80 tabular-nums",
                    variant === "modern" ? "tracking-tight font-bold" : "font-mono tracking-tight font-semibold"
                  )}
                >
                  {sizeBadge}
                </div>
              </div>
            ) : null}

            {/* Model badge (outside node, bottom-right) */}
            {!isCollapsed && !chromeless && modelBadge?.label && !isSelected ? (
              <div className="pointer-events-none absolute bottom-0 right-0 translate-y-[calc(100%+0.5rem)] z-30">
                <div
                  className={cn(
                    "flex items-center gap-1 text-xs text-foreground/80",
                    // Match node title typography (font family + tracking)
                    variant === "modern" ? "tracking-tight font-bold" : "font-mono tracking-tight font-semibold"
                  )}
                >
                  {modelBadge.Icon ? <modelBadge.Icon className="h-3 w-3 shrink-0 opacity-90" /> : null}
                  <span>{modelBadge.label}</span>
                </div>
              </div>
            ) : null}
          </div>
        </ContextMenuTrigger>
        <ContextMenuContent className={FM_CONTEXT_MENU_SURFACE}>
          {/* Sticky-mount: only build menu items after first right-click. */}
          {ctxMenuActivated ? (
          <>
          {/* Lock / Unlock (only original owner) */}
          {!isMultiSelect && isOriginalOwner && (
            <>
              <ContextMenuItem
                onClick={() => {
                  try {
                    const node = getNode(id) as any;
                    const prev = (node?.data ?? (data as any) ?? {}) as Record<string, unknown>;
                    updateNode(id, { data: { ...prev, locked: !isLocked } });
                  } catch {}
                }}
              >
                <Square size={12} className="text-muted-foreground" />
                <span>{isLocked ? t('Unlock') : t('Lock')}</span>
              </ContextMenuItem>
              <ContextMenuSeparator />
            </>
          )}
          {isMultiSelect ? (
            <>
              <ContextMenuSub>
                <ContextMenuSubTrigger className="gap-2" disabled={isMenuDisabledAll}>
                  <Square size={12} className="text-muted-foreground" />
                  <span>{t('Quick connect')}</span>
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className={FM_CONTEXT_MENU_SURFACE}>
                  <ContextMenuItem onClick={() => handleQuickConnectMulti('text')} disabled={isMenuDisabledAll}>
                    <MessageSquare size={12} className="text-muted-foreground" />
                    <span>{t('Text Model')}</span>
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => handleQuickConnectMulti('image')} disabled={isMenuDisabledAll}>
                    <ImageIcon size={12} className="text-muted-foreground" />
                    <span>{t('Image Model')}</span>
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => handleQuickConnectMulti('audio')} disabled={isMenuDisabledAll}>
                    <Music size={12} className="text-muted-foreground" />
                    <span>{t('Audio Model')}</span>
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => handleQuickConnectMulti('video')} disabled={isMenuDisabledAll}>
                    <PlayIcon size={12} className="text-muted-foreground" />
                    <span>{t('Video Model')}</span>
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => handleQuickConnectMulti('stickyNote')} disabled={isMenuDisabledAll}>
                    <StickyNote size={12} className="text-muted-foreground" />
                    <span>{t('Sticky Note')}</span>
                  </ContextMenuItem>
                </ContextMenuSubContent>
              </ContextMenuSub>
              <ContextMenuSeparator />
              <ContextMenuItem variant="destructive" onClick={isMenuDisabledAll ? undefined : () => setBulkDeleteConfirm(true)} disabled={isMenuDisabledAll}>
                <TrashIcon size={12} />
                <span>{t('Delete')}</span>
              </ContextMenuItem>
            </>
          ) : (
            <>
              {/* Save the node's media into the asset library (move into a folder) */}
              {(() => {
                const d = data as any;
                const rawUrl =
                  d?.generated?.url ||
                  d?.content?.url ||
                  d?.sourceUrl ||
                  (Array.isArray(d?.generated?.urls) ? d.generated.urls[0] : undefined);
                if (!rawUrl || typeof rawUrl !== 'string') return null;
                if (rawUrl.startsWith('Asset://')) return null;
                const mime = String(d?.content?.type || d?.generated?.type || '').toLowerCase();
                let mediaType: 'image' | 'video' | 'audio' | null = null;
                if (type === 'image') mediaType = 'image';
                else if (type === 'video') mediaType = 'video';
                else if (type === 'audio') mediaType = 'audio';
                else if (type === 'upload') {
                  const ft = String(d?.fileType || '').toLowerCase();
                  if (ft === 'image' || mime.startsWith('image/')) mediaType = 'image';
                  else if (ft === 'video' || mime.startsWith('video/')) mediaType = 'video';
                  else if (ft === 'audio' || mime.startsWith('audio/')) mediaType = 'audio';
                }
                if (!mediaType) return null;
                return (
                  <>
                    <SaveToLibrarySubmenu
                      url={rawUrl}
                      mediaType={mediaType}
                      name={typeof title === 'string' ? title : ''}
                      scope={(project as any)?.orgId ? 'team' : 'personal'}
                      projectId={(project as any)?.id ?? null}
                      disabled={isMenuDisabledAll}
                    />
                    <ContextMenuSeparator />
                  </>
                );
              })()}
              <ContextMenuSub>
                <ContextMenuSubTrigger className="gap-2" disabled={isMenuDisabledAll}>
                  <Square size={12} className="text-muted-foreground" />
                  <span>{t('Quick connect')}</span>
                </ContextMenuSubTrigger>
                <ContextMenuSubContent className={FM_CONTEXT_MENU_SURFACE}>
                  <ContextMenuItem onClick={() => handleQuickConnect('text')} disabled={isMenuDisabledAll}>
                    <MessageSquare size={12} className="text-muted-foreground" />
                    <span>{t('Text Model')}</span>
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => handleQuickConnect('image')} disabled={isMenuDisabledAll}>
                    <ImageIcon size={12} className="text-muted-foreground" />
                    <span>{t('Image Model')}</span>
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => handleQuickConnect('audio')} disabled={isMenuDisabledAll}>
                    <Music size={12} className="text-muted-foreground" />
                    <span>{t('Audio Model')}</span>
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => handleQuickConnect('video')} disabled={isMenuDisabledAll}>
                    <PlayIcon size={12} className="text-muted-foreground" />
                    <span>{t('Video Model')}</span>
                  </ContextMenuItem>
                  <ContextMenuItem onClick={() => handleQuickConnect('stickyNote')} disabled={isMenuDisabledAll}>
                    <StickyNote size={12} className="text-muted-foreground" />
                    <span>{t('Sticky Note')}</span>
                  </ContextMenuItem>
                </ContextMenuSubContent>
              </ContextMenuSub>
              <ContextMenuSeparator />
              {Array.isArray(contextMenuItems) && contextMenuItems.length ? (
                <>
                  {contextMenuItems.map((item, idx) => (
                    <ContextMenuItem key={`extra-${idx}`} onClick={isMenuDisabledAll || item.disabled ? undefined : item.onClick} disabled={isMenuDisabledAll || item.disabled}>
                      {item.icon ? (
                        <div className="text-muted-foreground">{item.icon}</div>
                      ) : null}
                      <span>{item.label}</span>
                    </ContextMenuItem>
                  ))}
                  <ContextMenuSeparator />
                </>
              ) : null}
              <ContextMenuItem onClick={isMenuDisabledAll ? undefined : () => duplicateNode(id)} disabled={isMenuDisabledAll || isProcessing}>
                <CopyIcon size={12} />
                <span>{t('Duplicate')}</span>
              </ContextMenuItem>
              <ContextMenuItem onClick={isMenuDisabledAll ? undefined : () => cloneNode(id)} disabled={isMenuDisabledAll || isProcessing}>
                <CopyPlus size={12} />
                <span>{t('Clone (with upstream)')}</span>
              </ContextMenuItem>
              <ContextMenuItem onClick={isMenuDisabledAll ? undefined : handleFocus} disabled={isMenuDisabledAll}>
                <EyeIcon size={12} />
                <span>{t('Focus')}</span>
              </ContextMenuItem>
              {/* 发送图片到底部对话框 */}
              {(() => {
                const d = data as any;
                const imgUrl =
                  d?.generated?.url ||
                  d?.content?.url ||
                  d?.sourceUrl ||
                  (Array.isArray(d?.generated?.urls) ? d.generated.urls[0] : undefined);
                if (!imgUrl || typeof imgUrl !== 'string') return null;
                // 仅图片类型才显示（不含视频/音频 MIME）
                const mime = String(d?.content?.type || d?.generated?.type || '').toLowerCase();
                if (mime && (mime.startsWith('video/') || mime.startsWith('audio/'))) return null;
                return (
                  <ContextMenuItem
                    onClick={() => {
                      try {
                        window.dispatchEvent(
                          new CustomEvent('prompt_bar_attach_image', { detail: { url: imgUrl, nodeId: id } }),
                        );
                      } catch {}
                    }}
                  >
                    <SendHorizonal size={12} />
                    <span>{t('Send to prompt bar')}</span>
                  </ContextMenuItem>
                );
              })()}
              <ContextMenuSeparator />
              <ContextMenuItem onClick={isMenuDisabledAll ? undefined : handleDisconnectIncoming} disabled={isMenuDisabledAll || isProcessing}>
                <ArrowLeft size={12} />
                <span>{t('Disconnect left edges')}</span>
              </ContextMenuItem>
              <ContextMenuItem onClick={isMenuDisabledAll ? undefined : handleDisconnectOutgoing} disabled={isMenuDisabledAll || isProcessing}>
                <ArrowRight size={12} />
                <span>{t('Disconnect right edges')}</span>
              </ContextMenuItem>
              <ContextMenuSeparator />
              <ContextMenuItem onClick={isMenuDisabledAll ? undefined : handleDeleteSingle} variant="destructive" disabled={isMenuDisabledAll}>
                <TrashIcon size={12} />
                <span>{t('Delete')}</span>
              </ContextMenuItem>
            </>
          )}
          </>
          ) : null}
        </ContextMenuContent>
      </ContextMenu>
      <Handle
        type="source"
        position={Position.Right}
        isConnectable={!isMenuDisabled}
        className={isMenuDisabled ? 'opacity-50 pointer-events-none' : undefined}
      />
      {/* 批量删除确认 */}
      {bulkDeleteConfirm ? (
        <DeleteConfirmDialog
          open={bulkDeleteConfirm}
          onOpenChange={setBulkDeleteConfirm}
          selectedCount={getSelectedCount()}
          onConfirm={handleDeleteSelected}
        />
      ) : null}
    </>
  );
};

// Stable shallow comparator for NodeLayout. Skips re-renders when the parent
// node component re-renders without changing any visible NodeLayout prop.
// Important callers (image/video/text/etc transforms) should keep `data`,
// `toolbar`, `children`, etc. referentially stable across re-renders for this
// to be effective; ReactFlow already memoizes node `data` in its store.
function areNodeLayoutPropsEqual(
  prev: NodeLayoutProps,
  next: NodeLayoutProps,
): boolean {
  if (prev.id !== next.id) return false;
  if (prev.type !== next.type) return false;
  if (prev.title !== next.title) return false;
  if (prev.variant !== next.variant) return false;
  if (prev.className !== next.className) return false;
  if (prev.aspectRatio !== next.aspectRatio) return false;
  if (prev.collapsedAspectRatio !== next.collapsedAspectRatio) return false;
  if (prev.data !== next.data) return false;
  if (prev.toolbar !== next.toolbar) return false;
  if (prev.children !== next.children) return false;
  if (prev.titleBubble !== next.titleBubble) return false;
  if (prev.collapsedSummary !== next.collapsedSummary) return false;
  if (prev.chromeless !== next.chromeless) return false;
  if (prev.contextMenuItems !== next.contextMenuItems) return false;
  return true;
}

export const NodeLayout = memo(NodeLayoutImpl, areNodeLayoutPropsEqual);
